// <!-- AI-authored: Darby's Claude -->
//
// Download-engine startup events: how long the persisted video store actually
// takes to load on real devices.
//
// This exists to give the engine's hydrate BUDGET an empirical basis. The
// budget bounds how long startup waits for the store before carrying on
// without the media rejoin (see downloadEngine.ts) — a number that today is a
// guess. Sentry only hears about OVERRUNS, which says the budget was exceeded
// but never what normal looks like; this event reports every completed load,
// so the worker's nightly field-RUM pull can chart the real distribution and
// the budget can be tuned from device data instead of intuition.

import { Capacitor } from "@capacitor/core";
import { type AnalyticsProperties, captureClientEvent } from "./analyticsEvent";

export const DOWNLOAD_ENGINE_EVENTS = {
  hydrateCompleted: "engine hydrate completed",
} as const;

export type EngineHydrateProperties = AnalyticsProperties & {
  /** Wall-clock time of the FULL store load, budget or no budget. */
  hydrate_ms: number;
  /** Videos in the loaded store — the divisor that makes durations comparable. */
  item_count: number;
  /** True when the load finished after startup had stopped waiting for it. */
  over_budget: boolean;
};

export function captureEngineHydrateCompleted(
  properties: EngineHydrateProperties,
): void {
  captureClientEvent({
    event: DOWNLOAD_ENGINE_EVENTS.hydrateCompleted,
    sourceFamily: "download_engine",
    properties: {
      ...properties,
      // Stamped here, not by the caller: the budget question this series
      // answers is about NATIVE store loads (per-video file IO). The same
      // engine also runs in mobile-Safari sessions of the production site,
      // whose localStorage loads are fast enough to drown the native signal
      // in a mixed series — the rollup filters on this flag.
      native: Capacitor.isNativePlatform(),
    },
  });
}
